import React, { useState, useEffect, useCallback } from "react";
import {
  Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Box, Chip, TextField, MenuItem, Select, FormControl,
  InputLabel, IconButton, Tooltip, Button, Divider, CircularProgress,
  ToggleButtonGroup, ToggleButton, Autocomplete,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { instance } from "../../axios";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const YEAR = dayjs().year();
const QUARTERS = [
  { label: "T1", start: `${YEAR}-01-01`, end: `${YEAR}-03-31` },
  { label: "T2", start: `${YEAR}-04-01`, end: `${YEAR}-06-30` },
  { label: "T3", start: `${YEAR}-07-01`, end: `${YEAR}-09-30` },
  { label: "T4", start: `${YEAR}-10-01`, end: `${YEAR}-12-31` },
];

const REPORT_TYPES = [
  { value: "vigilancia_epidemiologica", label: "Vigilancia Epidemiológica" },
];

const statusColor = { pending: "warning", done: "success", error: "error" };
const statusLabel = { pending: "Pendiente", done: "Listo", error: "Error" };

const ReportList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [companies, setCompanies] = useState([]);

  const [type, setType] = useState("vigilancia_epidemiologica");
  const [company, setCompany] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeQuarter, setActiveQuarter] = useState(null);

  const handleQuarterSelect = (_, value) => {
    if (!value) return;
    const q = QUARTERS.find((q) => q.label === value);
    setStartDate(q.start);
    setEndDate(q.end);
    setActiveQuarter(value);
  };

  const handleStartDate = (e) => { setStartDate(e.target.value); setActiveQuarter(null); };
  const handleEndDate = (e) => { setEndDate(e.target.value); setActiveQuarter(null); };

  useEffect(() => {
    instance.get("/company?limit=500&offset=0&query=").then((res) => setCompanies(res.data?.data || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    instance.get("/report")
      .then((res) => setRows(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = () => {
    if (!startDate || !endDate) return;
    setGenerating(true);
    instance.post("/report", { type, startDate, endDate, companyId: company?.id || null })
      .then(() => { load(); toast.success("Reporte generado exitosamente."); })
      .catch(() => toast.error("Error al generar el reporte. Problema con el almacenamiento, intente de nuevo."))
      .finally(() => setGenerating(false));
  };

  const handleDownload = (id) => {
    setDownloading(id);
    instance.get(`/report/${id}/download`)
      .then((res) => window.open(res.data.url, "_blank"))
      .finally(() => setDownloading(null));
  };

  return (
    <Box display="flex" flexDirection="column" gap={3} width="100%">

      {/* Formulaire génération */}
      <Paper sx={{ p: 3, backgroundColor: "white" }}>
        <Typography variant="h6" gutterBottom>Generar reporte</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Tipo de reporte</InputLabel>
            <Select value={type} label="Tipo de reporte" onChange={(e) => setType(e.target.value)}>
              {REPORT_TYPES.map((r) => (
                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            size="small"
            options={companies}
            getOptionLabel={(o) => o.name || ""}
            value={company}
            onChange={(_, val) => setCompany(val)}
            sx={{ width: 260 }}
            renderInput={(params) => (
              <TextField {...params} label="Empresa (opcional)" />
            )}
          />

          <ToggleButtonGroup
            value={activeQuarter}
            exclusive
            onChange={handleQuarterSelect}
            size="small"
          >
            {QUARTERS.map((q) => (
              <ToggleButton key={q.label} value={q.label} sx={{ px: 1.5, fontWeight: 600 }}>
                {q.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            size="small"
            label="Desde"
            type="date"
            value={startDate}
            onChange={handleStartDate}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />
          <TextField
            size="small"
            label="Hasta"
            type="date"
            value={endDate}
            onChange={handleEndDate}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 155 }}
          />

          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || !startDate || !endDate}
            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {generating ? "Generando..." : "Generar"}
          </Button>
        </Box>
      </Paper>

      {/* Liste des rapports */}
      <Paper sx={{ p: 3, backgroundColor: "white" }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Reportes generados</Typography>
          <Tooltip title="Actualizar">
            <IconButton onClick={load} size="small"><RefreshIcon /></IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Período</TableCell>
                <TableCell>Generado por</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Visualizar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center">Cargando...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">Sin reportes</TableCell></TableRow>
              ) : rows.map((row) => {
                const typeLabel = REPORT_TYPES.find((r) => r.value === row.type)?.label || row.type;
                const companyName = row.params?.companyId
                  ? (companies.find((c) => c.id === row.params.companyId)?.name || `#${row.params.companyId}`)
                  : "Todas";
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {dayjs(row.createdAt).format("DD/MM/YYYY HH:mm")}
                    </TableCell>
                    <TableCell>{typeLabel}</TableCell>
                    <TableCell>{companyName}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.params?.startDate} — {row.params?.endDate}
                    </TableCell>
                    <TableCell>{row.creator?.displayName || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabel[row.status] || row.status}
                        color={statusColor[row.status] || "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {row.status === "done" && (
                        <Tooltip title="Visualizar reporte">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(row.id)}
                            disabled={downloading === row.id}
                          >
                            {downloading === row.id
                              ? <CircularProgress size={16} />
                              : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ReportList;
